import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { X } from 'lucide-react';
import './TicketFilterBar.scss';

const STATUS_OPTIONS = [
    { value: 'reported', label: 'Reported' },
    { value: 'open', label: 'Open' },
    { value: 'reopened', label: 'Reopened' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'resolved', label: 'Resolved' },
];

// Same four options for both panels that use this filter bar — each backend query has its own
// default (newest-first for assigned tickets, least-recently-updated i.e. most-stale-first for
// escalated/stale) when sortBy isn't explicitly one of these.
const SORT_OPTIONS = [
    { value: 'created_desc', label: 'Newest first' },
    { value: 'created_asc', label: 'Oldest first' },
    { value: 'modified_desc', label: 'Recently updated' },
    { value: 'modified_asc', label: 'Least recently updated' },
];

const selectSx = {
    fontSize: '0.78rem',
    color: 'var(--dbp-text-sec)',
    background: 'var(--dbp-bg-sunken)',
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { borderColor: 'var(--dbp-border)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--dbp-accent-dim-h)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--dbp-accent)' },
    '.MuiSelect-icon': { color: 'var(--dbp-text-muted)' },
};

const menuProps = {
    PaperProps: {
        sx: {
            background: 'var(--dbp-bg)',
            color: 'var(--dbp-text-sec)',
            border: '1px solid var(--dbp-border)',
            '.MuiMenuItem-root:hover': { background: 'var(--dbp-accent-dim)' },
            '.MuiMenuItem-root.Mui-selected': { background: 'var(--dbp-accent-dim)' },
        },
    },
};

export default function TicketFilterBar({
    statusValue,
    onStatusChange,
    labelOptions,
    selectedLabelIds,
    onLabelsChange,
    assigneeOptions,
    selectedAssigneeId,
    onAssigneeChange,
    sortBy,
    onSortChange,
}) {
    const className = 'ticketFilterBar';
    const hasActiveFilters =
        Boolean(statusValue) || (selectedLabelIds && selectedLabelIds.length > 0) || Boolean(selectedAssigneeId);

    const clearAll = () => {
        onStatusChange(null);
        onLabelsChange([]);
        if (onAssigneeChange) onAssigneeChange(null);
    };

    return (
        <div className={className}>
            <Select
                size="small"
                displayEmpty
                value={statusValue || ''}
                onChange={(e) => onStatusChange(e.target.value || null)}
                sx={selectSx}
                MenuProps={menuProps}
                className={className + '__select'}
            >
                <MenuItem value="">All statuses</MenuItem>
                {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>

            <Select
                size="small"
                multiple
                displayEmpty
                value={selectedLabelIds || []}
                onChange={(e) => onLabelsChange(e.target.value)}
                renderValue={(selected) =>
                    selected.length === 0 ? 'All labels' : `${selected.length} label${selected.length > 1 ? 's' : ''}`
                }
                sx={selectSx}
                MenuProps={menuProps}
                className={className + '__select'}
            >
                {(labelOptions || []).map((label) => (
                    <MenuItem key={label.id} value={label.id}>
                        <Checkbox
                            size="small"
                            checked={(selectedLabelIds || []).includes(label.id)}
                            sx={{ color: 'var(--dbp-text-muted)', '&.Mui-checked': { color: 'var(--dbp-accent)' } }}
                        />
                        <ListItemText primary={label.name} />
                    </MenuItem>
                ))}
            </Select>

            {assigneeOptions && (
                <Select
                    size="small"
                    displayEmpty
                    value={selectedAssigneeId || ''}
                    onChange={(e) => onAssigneeChange(e.target.value || null)}
                    sx={selectSx}
                    MenuProps={menuProps}
                    className={className + '__select'}
                >
                    <MenuItem value="">All assignees</MenuItem>
                    {assigneeOptions.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                            {user.label}
                        </MenuItem>
                    ))}
                </Select>
            )}

            {onSortChange && (
                <Select
                    size="small"
                    value={sortBy || SORT_OPTIONS[0].value}
                    onChange={(e) => onSortChange(e.target.value)}
                    sx={selectSx}
                    MenuProps={menuProps}
                    className={className + '__select'}
                >
                    {SORT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            )}

            {hasActiveFilters && (
                <button type="button" className={className + '__clear'} onClick={clearAll}>
                    <X />
                    Clear
                </button>
            )}
        </div>
    );
}
